import { useState, useCallback } from "react";
import {
  ambassadorService,
  Ambassador,
  AmbassadorUpdatePayload,
  AmbassadorListParams,
  AmbassadorMonthlyStatsResponse,
  AmbassadorPeriod,
} from "../../../../services/ambassadorService";

export interface AmbassadorFilters {
  name: string;
  email: string;
  level: string;
  promo_code: string;
  status: string;
  phone: string;
}

const EMPTY_FILTERS: AmbassadorFilters = {
  name: "",
  email: "",
  level: "",
  promo_code: "",
  status: "",
  phone: "",
};

export const useAmbassadors = (pageSize = 50) => {
  const [ambassadors, setAmbassadors] = useState<Ambassador[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<AmbassadorFilters>(EMPTY_FILTERS);
  const [summary, setSummary] = useState({
    total_ambassadors: 0,
    total_filleuls: 0,
    total_active_filleuls: 0,
    total_revenus_izi: 0,
    total_salary: 0,
    total_cost: 0,
    total_profit_net: 0,
  });
  const [monthlyStats, setMonthlyStats] = useState<AmbassadorMonthlyStatsResponse | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState<AmbassadorPeriod>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAmbassadors = useCallback(
    async (
      pageIndex = page,
      filterOverride?: AmbassadorFilters,
      periodOverride?: { period?: AmbassadorPeriod; startDate?: string; endDate?: string }
    ) => {
      setLoading(true);
      setError(null);
      try {
        const activeFilters = filterOverride ?? filters;
        const activePeriod = periodOverride?.period ?? period;
        const activeStart = periodOverride?.startDate ?? startDate;
        const activeEnd = periodOverride?.endDate ?? endDate;
        const params: AmbassadorListParams = {
          skip: pageIndex * pageSize,
          limit: pageSize,
        };
        if (activeFilters.name.trim()) params.name = activeFilters.name.trim();
        if (activeFilters.email.trim()) params.email = activeFilters.email.trim();
        if (activeFilters.level) params.level = activeFilters.level;
        if (activeFilters.promo_code.trim()) params.promo_code = activeFilters.promo_code.trim();
        if (activeFilters.status.trim()) params.status = activeFilters.status.trim();
        if (activeFilters.phone.trim()) params.phone = activeFilters.phone.trim();
        if (activeStart || activeEnd) {
          if (activeStart) params.start_date = activeStart;
          if (activeEnd) params.end_date = activeEnd;
        } else if (activePeriod) {
          params.period = activePeriod;
        }

        const data = await ambassadorService.listAmbassadors(params);
        setAmbassadors(data?.items || []);
        setTotal(data?.total || 0);
        setSummary(
          data?.summary || {
            total_ambassadors: 0,
            total_filleuls: 0,
            total_active_filleuls: 0,
            total_revenus_izi: 0,
            total_salary: 0,
            total_cost: 0,
            total_profit_net: 0,
          }
        );
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || "Erreur lors du chargement des ambassadeurs");
      } finally {
        setLoading(false);
      }
    },
    [filters, page, pageSize, period, startDate, endDate]
  );

  const loadMonthlyStats = useCallback(
    async (year = selectedYear) => {
      setStatsLoading(true);
      try {
        const data = await ambassadorService.getMonthlyStats(year);
        setMonthlyStats(data);
        if (data?.available_years?.length && !data.available_years.includes(year)) {
          setSelectedYear(data.available_years[0]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || "Erreur lors du chargement des statistiques");
      } finally {
        setStatsLoading(false);
      }
    },
    [selectedYear]
  );

  const updateFilter = (key: keyof AmbassadorFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    loadAmbassadors(0, EMPTY_FILTERS);
  };

  const applyFilters = () => {
    setPage(0);
    loadAmbassadors(0);
  };

  const changePage = (newPage: number) => {
    setPage(newPage);
    loadAmbassadors(newPage);
  };

  const changeYear = (year: number) => {
    setSelectedYear(year);
    loadMonthlyStats(year);
  };

  const changePeriod = (newPeriod: AmbassadorPeriod) => {
    setPeriod(newPeriod);
    setStartDate("");
    setEndDate("");
    setPage(0);
    loadAmbassadors(0, undefined, { period: newPeriod, startDate: "", endDate: "" });
  };

  const changeDateRange = (newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    setPeriod("");
    setPage(0);
    loadAmbassadors(0, undefined, { period: "", startDate: newStart, endDate: newEnd });
  };

  const updateAmbassador = async (userId: string, payload: AmbassadorUpdatePayload) => {
    try {
      await ambassadorService.updateAmbassador(userId, payload);
      await loadAmbassadors(page);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Erreur lors de la mise à jour");
      return false;
    }
  };

  return {
    ambassadors,
    total,
    page,
    pageSize,
    filters,
    summary,
    monthlyStats,
    selectedYear,
    period,
    startDate,
    endDate,
    loading,
    statsLoading,
    error,
    loadAmbassadors,
    loadMonthlyStats,
    updateFilter,
    resetFilters,
    applyFilters,
    changePage,
    changeYear,
    changePeriod,
    changeDateRange,
    updateAmbassador,
  };
};
